<template>
  <b-tabs v-model="tabIndex">
    <b-tab :title="`Open Queries: ${openQueries.length}`" :title-link-class="queriesOpen()">
      <FormQueriesCommentForm v-if="value" :siteTeamList="siteTeamList" @cancel="cancelQuery" @submit="createQuery" :getOffsetValue="getOffsetValue" />
      <div v-else>
        <div v-if="openQueryId">
          <FormQueriesQueryDetails :lockStatus="lockStatus" :userNames="userNames" :comments="comments" :query="openQuery" @back="hideOpenQuery" @onQueryCommentCreate="onQueryCommentCreate" @onQueryClosed="onQueryClosed" />
        </div>
        <div v-else>
          <FormQueriesHeader v-if="filter" :title="`Filtered by field ID ${shortFilterId}`" @back="resetFilter" backTitle="Clear" />
          <b-row v-if="!openQueries.length" class="py-2">
            <b-col
              ><p>No queries created yet.<br />Select a field to create a query.</p></b-col
            >
          </b-row>
          <FormQueriesList v-else :queries="openQueries" :siteTeamList="siteTeamList" @view="viewOpenQuery" />
        </div>
      </div>
    </b-tab>
    <b-tab :title="`Closed Queries: ${closedQueries.length}`">
      <div v-if="closedQuery">
        <FormQueriesQueryDetails closed :lockStatus="lockStatus" :comments="comments" :userNames="userNames" :query="closedQuery" @back="hideClosedQuery" @onQueryReopened="onQueryReopened" />
      </div>
      <div v-else>
        <FormQueriesHeader v-if="filter" :title="`Filtered by field ID ${filter}`" @back="resetFilter" backTitle="Clear" />
        <b-row v-if="!closedQueries.length">
          <b-col class="py-2"><p>No queries closed yet.</p></b-col>
        </b-row>
        <FormQueriesList v-else :queries="closedQueries" :siteTeamList="siteTeamList" @view="viewClosedQuery" />
      </div>
    </b-tab>
  </b-tabs>
</template>

<script>
import sortBy from "lodash/sortBy";
import { mapGetters } from "vuex";
import FormQueriesList from "@/components/form/edc-form-queries/form-queries-list";
import FormQueriesQueryDetails from "@/components/form/edc-form-queries/form-queries-query-details";
import FormQueriesCommentForm from "@/components/form/edc-form-queries/form-queries-comment-form";
import FormQueriesHeader from "@/components/form/form-queries-header";

import { formFieldQueryStatus } from "@/constants/form";
import { AclRule } from "vue-acl";
import { CRA, DM, PI, CC, QUALITY, CraUB, PHV } from "@/constants/authRoles";
export default {
  name: "edc-form-queries",
  components: {
    FormQueriesList,
    FormQueriesQueryDetails,
    FormQueriesCommentForm,
    FormQueriesHeader
  },
  props: {
    value: String,
    queries: Array,
    filter: String,
    formId: String,
    studyId: String,
    comments: Array,
    siteId: String,
    getOffsetValue: Number,
    lockStatus: String
  },
  data() {
    return {
      tabIndex: 0,
      openQueryId: null,
      closedQueryId: null,
      userNames: {},
      siteTeamList: []
    };
  },
  async mounted() {
    const studyTeamData = await this.$api.getStudyTeamFact(this.$route.params.studyId);
    const siteTeamData = await this.$api.getUserNames(this.$route.params.studyId);
    studyTeamData.body.forEach(team => {
      this.userNames[team.id] = `${team.firstName} ${team.lastName}`;
    });
    siteTeamData.body.forEach(team => {
      this.userNames[team.id] = `${team.firstName} ${team.lastName}`;
    });
    await this.getTeamMembers();
  },
  computed: {
    ...mapGetters(["user"]),
    userAccess() {
      return new AclRule(this.user.userRole).generate();
    },
    canCraAccess() {
      this.$acl.change(`${CRA}`);
      return this.$acl.check("userAccess");
    },
    canCraUBAccess() {
      this.$acl.change(`${CraUB}`);
      return this.$acl.check("userAccess");
    },
    canDmAccess() {
      this.$acl.change(`${DM}`);
      return this.$acl.check("userAccess");
    },
    canPiAccess() {
      this.$acl.change(`${PI}`);
      return this.$acl.check("userAccess");
    },
    canCallCenterAccess() {
      this.$acl.change(`${CC}`);
      return this.$acl.check("userAccess");
    },
    canQualityAccess() {
      this.$acl.change(`${QUALITY}`);
      return this.$acl.check("userAccess");
    },
    canPharmacovigilanceAccess() {
      this.$acl.change(`${PHV}`);
      return this.$acl.check("userAccess");
    },
    shortFilterId() {
      if (this.filter) {
        // return this.filter.slice(0, 5).toUpperCase();
        return this.filter;
      }
      return "";
    },
    filteredQueries() {
      let filtered = this.queries;
      if (this.filter) {
        filtered = this.queries.filter(q => q.fieldId === this.filter);
        this.$forceUpdate();
      }
      return sortBy(filtered, ["openedOn"]).reverse();
    },
    openQueries() {
      const openQueries = [];
      this.filteredQueries.filter(query => {
        if (query.status === formFieldQueryStatus.OPEN || query.status === formFieldQueryStatus.REOPENED) {
          if (this.canCraAccess || this.canDmAccess || this.canPiAccess || this.canCallCenterAccess || this.canQualityAccess || this.canCraUBAccess || this.canPharmacovigilanceAccess) {
            openQueries.push(query);
          } else if (query.assigneeId) {
            if (this.user.id === query.assigneeId) {
              openQueries.push(query);
            }
          } else {
            openQueries.push(query);
          }
        }
      });
      return openQueries;
    },
    closedQueries() {
      const closedQueries = [];
      this.filteredQueries.filter(query => {
        if (query.status !== formFieldQueryStatus.OPEN && query.status !== formFieldQueryStatus.REOPENED) {
          if (this.canCraAccess || this.canDmAccess || this.canPiAccess || this.canCallCenterAccess || this.canCraUBAccess || this.canPharmacovigilanceAccess) {
            closedQueries.push(query);
          } else if (query.assigneeId) {
            if (this.user.id === query.assigneeId) {
              closedQueries.push(query);
            }
          } else {
            closedQueries.push(query);
          }
        }
      });
      return closedQueries;
    },
    openQuery() {
      return this.queries.find(q => q.queryId === this.openQueryId);
    },
    closedQuery() {
      return this.queries.find(q => q.queryId === this.closedQueryId);
    }
  },
  watch: {
    value(val) {
      if (val) {
        this.openQueryId = null;
        this.closedQueryId = null;
        this.tabIndex = 0;
      }
    },
    filter(val) {
      if (!this.value) {
        if (val) {
          this.shortFilterId = val;
          this.openQueryId = null;
          this.closedQueryId = null;
          this.tabIndex = 0;
        }
      }
    }
  },
  methods: {
    queriesOpen() {
      return "";
    },
    viewOpenQuery(queryId) {
      this.openQueryId = queryId;
      const field = this.openQueries.find(q => q.queryId === queryId);
      this.$emit("updateComment", queryId, field.fieldId);
    },
    hideOpenQuery() {
      this.$emit("updateQueries");
      this.openQueryId = null;
      this.$emit("resetFilter");
    },
    viewClosedQuery(queryId) {
      this.closedQueryId = queryId;
      const field = this.closedQueries.find(q => q.queryId === queryId);
      this.$emit("updateComment", queryId, field.fieldId);
    },
    hideClosedQuery() {
      this.closedQueryId = null;
      this.$emit("resetFilter");
    },
    createQuery(commentData, assigneeId) {
      const newQuery = {
        fieldId: this.value,
        formId: this.formId,
        comment: commentData,
        assigneeId: assigneeId
      };
      this.$emit("createQuery", newQuery);
      this.$emit("input", null);
    },
    onQueryCommentCreate(commentData) {
      this.$emit("createComment", commentData);
    },
    cancelQuery() {
      this.$emit("input", null);
      this.$emit("resetFilter");
    },
    resetFilter() {
      this.$emit("resetFilter");
      window.scrollTo(0, 0);
    },
    onQueryClosed(query) {
      this.openQueryId = null;
      this.$emit("onQueryClosed", query);
    },
    onQueryReopened(query) {
      this.closedQueryId = null;
      this.$emit("onQueryReopened", query);
    },
    getTeamMembers() {
      this.$api.getSiteTeamFact(this.$route.params.studyId, this.siteId).then(
        response => {
          this.siteTeamList = response.body;
        },
        error => {
          this.$toastr.error(error.body && error.body.message ? error.body.message : "Error while getting the site team details.");
        }
      );
    }
  }
};
</script>
<style>
a.text-warning:hover,
a.text-warning:focus,
.text-warning {
  color: #f5a623 !important;
}
</style>
